import {useLocation} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useMemo} from 'react'

import {
  GROUP_BY_PARAM,
  type Layout,
  LAYOUT_PARAM,
  SWIMLANE_BY_PARAM,
  type SavedViewUrlParams,
  writeSavedViewOverrides,
} from '../../utils/saved-view-url-params'

interface SavedViewUrlActions {
  setLayout: (layout: Layout) => void
  setGroupBy: (groupBy: string | null) => void
  setSwimlaneBy: (swimlaneBy: string | null) => void
  /**
   * Removes every URL param this hook manages in a single navigate. Sequential setter
   * calls each close over the same `search` and would race — the second navigate would
   * overwrite the first. Hosts wire this up as `onReset`.
   */
  clearOverrides: () => void
}

interface UseSavedViewUrlActionsInput {
  baseline: SavedViewUrlParams
  current: SavedViewUrlParams
}

/**
 * Writes changes relative to the host-supplied baseline and current configuration.
 * Hosts own how current configuration is resolved: Planning supplies the server-applied
 * configuration, while repository saved views use their explicit legacy URL adapter.
 */
export function useSavedViewUrlActions({
  baseline: baselineInput,
  current: currentInput,
}: UseSavedViewUrlActionsInput): SavedViewUrlActions {
  const {pathname, search} = useLocation()
  const navigate = useNavigate()
  const {layout: baselineLayout, groupBy: baselineGroupBy, swimlaneBy: baselineSwimlaneBy} = baselineInput
  const baseline: SavedViewUrlParams = useMemo(
    () => ({layout: baselineLayout, groupBy: baselineGroupBy, swimlaneBy: baselineSwimlaneBy}),
    [baselineLayout, baselineGroupBy, baselineSwimlaneBy],
  )
  const {layout: currentLayout, groupBy: currentGroupBy, swimlaneBy: currentSwimlaneBy} = currentInput
  const currentConfiguration: SavedViewUrlParams = useMemo(
    () => ({layout: currentLayout, groupBy: currentGroupBy, swimlaneBy: currentSwimlaneBy}),
    [currentGroupBy, currentLayout, currentSwimlaneBy],
  )

  const applyChanges = useCallback(
    (changes: Partial<SavedViewUrlParams>) => {
      const current = new URLSearchParams(search)
      const next = writeSavedViewOverrides(current, changes, baseline, currentConfiguration)
      const query = next.toString()
      if (query === current.toString()) return
      navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
    },
    [baseline, currentConfiguration, navigate, pathname, search],
  )

  const setLayout = useCallback((layout: Layout) => applyChanges({layout}), [applyChanges])
  const setGroupBy = useCallback((groupBy: string | null) => applyChanges({groupBy}), [applyChanges])
  const setSwimlaneBy = useCallback((swimlaneBy: string | null) => applyChanges({swimlaneBy}), [applyChanges])

  const clearOverrides = useCallback(() => {
    const current = new URLSearchParams(search)
    const next = new URLSearchParams(current)
    next.delete(LAYOUT_PARAM)
    next.delete(GROUP_BY_PARAM)
    if (baselineSwimlaneBy !== undefined) next.delete(SWIMLANE_BY_PARAM)
    const query = next.toString()
    if (query === current.toString()) return
    navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true})
  }, [baselineSwimlaneBy, navigate, pathname, search])

  return {
    setLayout,
    setGroupBy,
    setSwimlaneBy,
    clearOverrides,
  }
}
